import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Star, ArrowRight, Calendar, Heart, Users, Gift, Camera, Mic, CheckSquare, Music, Shirt, Globe } from 'lucide-react'
import MarketingHeader from '../components/MarketingHeader'
import FallingPetals from '../components/FallingPetals'
import MarketingFooter from '../components/MarketingFooter'
import { supabase } from '../lib/supabase'

const features = [
  { icon:Calendar,    title:'Multi-Event Manager',    desc:'Traditional, white wedding, reception — manage every event in one place with separate guest lists.' },
  { icon:Heart,       title:'Digital Invitations',    desc:'Beautifully designed, culturally inspired invite templates sent via link, image, or PDF.' },
  { icon:Users,       title:'Smart Guest & RSVP',     desc:'Know exactly who\'s coming, per event. Unique invite links that can\'t be shared or misused.' },
  { icon:Gift,        title:'Registry & Gift Log',    desc:'A built-in wishlist, cash gift links, and a full thank-you tracker — all in one place.' },
  { icon:Camera,      title:'Shared Photo Gallery',   desc:'Guests upload photos from their phones via a link — no app download needed.' },
  { icon:Mic,         title:'Dress Code & Programme', desc:'Communicate Aso-Ebi colours and your full Order of Service digitally via QR code.' },
  { icon:CheckSquare, title:'Wedding To-Do Checklist',desc:'A smart checklist organized by timeline — 12 months out to the final week. Know exactly what to do and when.' },
  { icon:Music,       title:'Vow Writer',             desc:'Write and save your personal vows privately inside the app. Share with your partner when you\'re ready.' },
  { icon:Shirt,       title:'Aso Ebi Coordination',  desc:'Manage fabric orders, payments, style lookbooks, and collection points — all from one dashboard.' },
]

const testimonials = [
  { name:'Adaeze & Chukwuemeka', location:'Lagos, Nigeria', text:'We had 4 events and Okoiya kept everything organized without a single WhatsApp headache. Our guests were amazed.' },
  { name:'Funmi & David',        location:'London, UK',     text:'The invite system is genius. No one could forward their link and bring uninvited guests. Lifesaver.' },
  { name:'Grace & Michael',      location:'Accra, Ghana',   text:"We really loved that we could share our story and song. It's a really beautiful feature for real." },
]

const FAQS = [
  { q:'Is Okoiya really free to start?', a:'Yes — sign up, set up your wedding, add one event, manage up to 5 guests and 5 registry items, upload studio photos, and publish your guest portal completely free. Premium unlocks unlimited events, guests, and registry items, plus budget tracking, Aso Ebi, the vow writer, QR check-in, live streaming, and more.' },
  { q:'Do my guests need to create an account?', a:'Never. Guests access everything — RSVPs, registry, programme, photos — through one link. No downloads, no sign-ups, no friction.' },
  { q:'How does the personal RSVP link work?', a:'Every guest gets their own unique link via WhatsApp. When they open it, they see a personalised invite with their name and the events they\'re invited to. One tap to confirm. Nobody can share their link to gate-crash.' },
  { q:'Can both of us manage the wedding together?', a:'Yes — you can invite your partner from the Settings page. They create their own account and get full co-admin access to everything.' },
  { q:'What happens on the wedding day?', a:'Premium couples get a live Guest Check-In page where an usher can search and mark guests as arrived per event. You also get a live streaming link for guests watching remotely.' },
  { q:'How does the registry work for guests?', a:'Guests open your wedding portal through their invite link. They scroll to the Registry tab — no separate link needed — browse items, filter by price, and reserve with their name. You see reservations instantly.' },
  { q:'Is my data safe and private?', a:'Your wedding details are only visible to people you share your link with. Guest lists, budget, and personal information are protected by row-level security in our database and are never shared or sold.' },
  { q:'Can I use my own invitation design?', a:'Yes — Premium users can upload a custom PNG or JPG invitation image. Free users get access to 3 beautifully designed templates; Premium unlocks 8 templates plus the upload option.' },
  { q:'What currency does the budget tracker support?', a:'NGN (₦), USD ($), GBP (£), EUR (€), GHS (₵), KES (KSh), and ZAR (R). You can switch currency at any time.' },
  { q:'Can I export my guest list and budget?', a:'Yes — both can be exported as CSV from their respective pages. The programme can be downloaded as a PDF. These work on free and premium plans.' },
]

function FAQItem({ faq, isOpen, onToggle }) {
  return (
    <div style={{ background:'white', border:'1px solid #E5E0D8', borderRadius:'1rem', overflow:'hidden' }}>
      <button onClick={onToggle} style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'1.125rem 1.375rem', background:'none', border:'none', cursor:'pointer', textAlign:'left', gap:'1rem' }}>
        <span style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:600, fontSize:'0.95rem', color:'#1A1A1A', flex:1 }}>{faq.q}</span>
        <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'1.25rem', color:'#C4826A', flexShrink:0, display:'inline-block', transform:isOpen?'rotate(45deg)':'none', transition:'transform 0.2s' }}>+</span>
      </button>
      {isOpen && (
        <div style={{ padding:'0 1.375rem 1.125rem', borderTop:'1px solid #F3F0EA' }}>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.875rem', color:'#6B6B6B', lineHeight:1.75, margin:'0.75rem 0 0' }}>{faq.a}</p>
        </div>
      )}
    </div>
  )
}

function FAQList() {
  const [open, setOpen] = useState(null)
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
      {FAQS.map((faq,i) => <FAQItem key={i} faq={faq} isOpen={open===i} onToggle={()=>setOpen(open===i?null:i)}/>)}
    </div>
  )
}

import { useSeo } from '../lib/seo'

// Single testimonial card — shared by the static grid and the auto-slider.
function TestimonialCard({ t }) {
  return (
    <div style={{ background:'#FAFAF8', border:'1px solid #E5E0D8', borderRadius:'1.5rem', padding:'2rem', position:'relative', overflow:'hidden', height:'100%', boxSizing:'border-box' }}>
      <span style={{ position:'absolute', top:16, left:20, fontFamily:'Georgia, serif', fontSize:'4rem', color:'rgba(196,130,106,0.12)', lineHeight:1, userSelect:'none' }}>"</span>
      <div style={{ display:'flex', gap:'2px', marginBottom:'1rem' }}>
        {[...Array(Math.max(1, Math.min(5, t.rating || 5)))].map((_,j)=><Star key={j} size={14} fill="#C4826A" color="#C4826A"/>)}
      </div>
      <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.9rem', color:'#3A3A3A', lineHeight:1.75, fontStyle:'italic', margin:'0 0 1.5rem', position:'relative', zIndex:1 }}>"{t.quote || t.text}"</p>
      <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
        <div style={{ width:36, height:36, borderRadius:'50%', background:'#C4826A', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <span style={{ fontFamily:'Gibs, serif', fontSize:'0.85rem', color:'white' }}>{(t.name||'').split(' ').slice(0,2).map(w=>w[0]).join('')}</span>
        </div>
        <div>
          <p style={{ fontFamily:'Gibs, serif', fontSize:'0.85rem', color:'#1A1A1A', margin:0 }}>{t.name}</p>
          {t.location && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9BAF9B', margin:0, fontWeight:600 }}>{t.location}</p>}
        </div>
      </div>
    </div>
  )
}

export default function LandingPage() {
  useSeo({ description: 'Okoiya gives couples one beautiful place to manage every moment of their wedding — invitations, guests, RSVPs, Aso Ebi, registry and budget.', path: '/' })

  // Admin-managed testimonials (falls back to the seed list before the table exists).
  const [tList, setTList] = useState(null)
  const [tVisible, setTVisible] = useState(true)
  useEffect(() => {
    supabase.from('testimonials').select('*').eq('published', true).order('sort_order').order('created_at')
      .then(({ data }) => setTList(data || []), () => setTList([]))
    supabase.from('admin_settings').select('value').eq('key', 'testimonials').maybeSingle()
      .then(({ data }) => { if (data?.value?.visible === false) setTVisible(false) }, () => {})
  }, [])
  const liveTestimonials = (tList && tList.length) ? tList : testimonials
  const showTestimonials = tVisible && liveTestimonials.length > 0
  const testimonialsSlider = liveTestimonials.length > 3

  return (
    <div style={{ background:'#FAFAF8', minHeight:'100vh' }}>
      <MarketingHeader/>

      <style>{`
        @keyframes heroFadeA { 0%{opacity:1} 45%{opacity:1} 50%{opacity:0} 95%{opacity:0} 100%{opacity:1} }
        @keyframes heroFadeB { 0%{opacity:0} 45%{opacity:0} 50%{opacity:1} 95%{opacity:1} 100%{opacity:0} }
        .tm-marquee { overflow:hidden; width:100%; -webkit-mask-image:linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent); mask-image:linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent); }
        .tm-track { display:flex; width:max-content; animation:tmScroll 55s linear infinite; }
        .tm-marquee:hover .tm-track { animation-play-state:paused; }
        .tm-item { width:355px; flex-shrink:0; margin-right:1.25rem; }
        @keyframes tmScroll { from{transform:translateX(0)} to{transform:translateX(-50%)} }
        @media (prefers-reduced-motion: reduce) { .tm-track { animation:none; } }
        @media (max-width:768px) {
          .hero-section { min-height:auto !important; padding:3.5rem 1.5rem 4rem !important; }
          .hero-wrap    { width:100% !important; padding-left:0 !important; }
          .hero-scrim   { background:linear-gradient(180deg, rgba(250,250,248,0.90) 0%, rgba(250,250,248,0.74) 42%, rgba(250,250,248,0.45) 100%) !important; }
          .hero-left    { text-align:center !important; align-items:center !important; max-width:none !important; margin:0 auto !important; }
          .hero-cta     { justify-content:center !important; }
          .hero-photo   { object-position:center 22% !important; }
          .social-strip { flex-direction:column !important; align-items:center !important; justify-content:center !important; gap:0.6rem !important; }
          .social-item  { width:100% !important; max-width:340px !important; }
          .band-inner   { padding:3.5rem 1.5rem !important; }
          .features-grid { grid-template-columns:1fr !important; }
          .testimonials-grid { grid-template-columns:1fr !important; }
        }
      `}</style>

      {/* ── HERO ── */}
      <section className="hero-section" style={{ position:'relative', overflow:'hidden', minHeight:'84vh', display:'flex', alignItems:'center', padding:'5rem 0' }}>
        {/* crossfading background photos */}
        <img className="hero-photo" src="/brand/hero-hands.webp" srcSet="/brand/hero-hands-1200.webp 1200w, /brand/hero-hands.webp 2000w" sizes="100vw" alt="A couple holding hands, wedding rings"
          style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', objectPosition:'right center', animation:'heroFadeA 18s ease-in-out infinite' }}/>
        <img className="hero-photo" src="/brand/hero-walk.webp" srcSet="/brand/hero-walk-1200.webp 1200w, /brand/hero-walk.webp 2000w" sizes="100vw" alt="A couple in traditional attire walking together" aria-hidden="true"
          style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', objectPosition:'right center', animation:'heroFadeB 18s ease-in-out infinite' }}/>
        {/* legibility scrim — opaque on the left, clears toward the couple on the right */}
        <div className="hero-scrim" style={{ position:'absolute', inset:0, background:'linear-gradient(90deg, rgba(250,250,248,0.96) 0%, rgba(250,250,248,0.82) 34%, rgba(250,250,248,0.32) 60%, rgba(250,250,248,0) 80%)' }}/>
        <FallingPetals/>

        <div className="hero-wrap" style={{ position:'relative', zIndex:1, width:'calc(100% - 48px)', maxWidth:1140, margin:'0 auto', paddingLeft:'1.5rem', boxSizing:'border-box' }}>
          <div className="hero-left" style={{ maxWidth:560, display:'flex', flexDirection:'column', alignItems:'flex-start' }}>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'#C4826A', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:'1.25rem' }}>BECOME AN OKOIYA COUPLE</p>

            <h1 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'clamp(2.5rem,5vw,4.2rem)', lineHeight:1.1, color:'#1A1A1A', margin:'0 0 1.25rem' }}>
              Your wedding, <em style={{ color:'#C4826A', fontStyle:'italic' }}>beautifully</em>{' '}
              organized from start to finish
            </h1>

            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'clamp(1rem,1.8vw,1.1rem)', color:'#5A5A5A', lineHeight:1.75, margin:'0 0 2.25rem', maxWidth:480 }}>
              Okoiya gives couples one beautiful place to manage every moment of their wedding.
            </p>

            <div className="hero-cta" style={{ display:'flex', gap:'0.875rem', flexWrap:'wrap' }}>
              <Link to="/signup" style={{ textDecoration:'none' }}>
                <button style={{ display:'flex', alignItems:'center', gap:'0.5rem', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.9rem', color:'white', background:'#C4826A', border:'none', borderRadius:100, padding:'0.875rem 2rem', cursor:'pointer' }}>
                  Get Started Free <ArrowRight size={16}/>
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── SOCIAL PROOF STRIP ── */}
      <section style={{ background:'white', borderTop:'1px solid #E5E0D8', borderBottom:'1px solid #E5E0D8', padding:'1.75rem 1.5rem' }}>
        <div className="social-strip" style={{ maxWidth:1040, margin:'0 auto', display:'flex', justifyContent:'center', alignItems:'stretch', gap:'0.75rem', flexWrap:'wrap' }}>
          {[
            { Icon:Users,    color:'#9BAF9B', bg:'rgba(155,175,155,0.16)', lead:'100+ couples',    rest:'planning with Okoiya' },
            { Icon:Calendar, color:'#C4826A', bg:'rgba(196,130,106,0.12)', lead:'Every ceremony',   rest:'traditional, white & court' },
            { Icon:Globe,    color:'#9BAF9B', bg:'rgba(155,175,155,0.16)', lead:'Loved worldwide',  rest:'Nigeria, UK, USA, Ghana & Canada' },
          ].map(({ Icon, color, bg, lead, rest }, i) => (
            <div key={i} className="social-item" style={{ display:'flex', alignItems:'center', gap:'0.7rem', background:'#FAFAF8', border:'1px solid #ECE6DE', borderRadius:'0.9rem', padding:'0.65rem 1rem' }}>
              <span style={{ width:34, height:34, borderRadius:'50%', background:bg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <Icon size={17} color={color}/>
              </span>
              <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#6B6B6B', lineHeight:1.4 }}>
                <strong style={{ color:'#1A1A1A', fontWeight:700 }}>{lead}</strong> {rest}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* ── FEATURES GRID ── */}
      <section style={{ padding:'5rem 2rem', maxWidth:1140, margin:'0 auto' }}>
        <div style={{ textAlign:'center', marginBottom:'3.5rem' }}>
          <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'clamp(1.8rem,3.5vw,2.5rem)', color:'#1A1A1A', margin:'0 0 0.75rem' }}>Everything your wedding needs</h2>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', color:'#6B6B6B', fontSize:'1rem', margin:0 }}>One app. Every event. Zero chaos.</p>
        </div>

        <div className="features-grid" style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'1.25rem' }}>
          {features.map(({ icon:Icon, title, desc },i)=>(
            <div key={i} style={{ background:'white', border:'1px solid #E5E0D8', borderRadius:'1.5rem', padding:'1.5rem', display:'flex', gap:'1rem', alignItems:'flex-start', transition:'transform 0.25s,box-shadow 0.25s', cursor:'default' }}
              onMouseEnter={e=>{ e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,0.08)' }}
              onMouseLeave={e=>{ e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='none' }}>
              <div style={{ width:44, height:44, borderRadius:'0.875rem', background:i%2===0?'rgba(155,175,155,0.15)':'rgba(196,130,106,0.12)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <Icon size={20} color={i%2===0?'#9BAF9B':'#C4826A'}/>
              </div>
              <div>
                <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1rem', color:'#1A1A1A', margin:'0 0 0.4rem' }}>{title}</h3>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.875rem', color:'#6B6B6B', lineHeight:1.65, margin:0 }}>{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── EMOTIONAL BAND ── */}
      <section style={{ position:'relative', overflow:'hidden' }}>
        <img src="/brand/band-moody.webp" srcSet="/brand/band-moody-1200.webp 1200w, /brand/band-moody.webp 2000w" sizes="100vw" alt="A couple sharing a quiet, golden moment"
          style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', objectPosition:'center 35%' }}/>
        <div style={{ position:'absolute', inset:0, background:'linear-gradient(90deg, rgba(26,12,8,0.82) 0%, rgba(26,12,8,0.55) 45%, rgba(26,12,8,0.30) 100%)' }}/>
        <div className="band-inner" style={{ position:'relative', zIndex:1, maxWidth:1140, margin:'0 auto', padding:'6rem 2rem' }}>
          <div style={{ maxWidth:620 }}>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'rgba(255,228,214,0.75)', letterSpacing:'0.12em', textTransform:'uppercase', margin:'0 0 1rem' }}>Every detail. Every moment.</p>
            <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'clamp(2rem,4.5vw,3.2rem)', lineHeight:1.18, color:'#FFFFFF', margin:'0 0 1.25rem', textShadow:'0 2px 16px rgba(0,0,0,0.35)' }}>
              Your love story deserves more than a <em style={{ color:'#E8B09A', fontStyle:'italic' }}>spreadsheet</em>.
            </h2>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'clamp(1rem,1.8vw,1.1rem)', color:'rgba(255,248,244,0.88)', lineHeight:1.75, margin:0, textShadow:'0 1px 10px rgba(0,0,0,0.3)' }}>
              From your first invite to your last dance, Okoiya holds it all together so you can stay present for the moments that matter.
            </p>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      {showTestimonials && (
      <section style={{ background:'white', padding:'5rem 0' }}>
        <h2 style={{ textAlign:'center', fontFamily:'Gibs, serif', fontWeight:400, fontSize:'clamp(1.8rem,3.5vw,2.5rem)', color:'#1A1A1A', margin:'0 2rem 3rem' }}>Couples love Okoiya</h2>
        {testimonialsSlider ? (
          /* Auto-sliding marquee when there are more than 3 */
          <div className="tm-marquee">
            <div className="tm-track">
              {[...liveTestimonials, ...liveTestimonials].map((t,i)=>(
                <div className="tm-item" key={i}><TestimonialCard t={t}/></div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ maxWidth:1140, margin:'0 auto', padding:'0 2rem' }}>
            <div className="testimonials-grid" style={{ display:'grid', gridTemplateColumns:`repeat(${liveTestimonials.length},1fr)`, gap:'1.25rem' }}>
              {liveTestimonials.map((t,i)=><TestimonialCard key={i} t={t}/>)}
            </div>
          </div>
        )}
      </section>
      )}

      {/* ── FAQ ── */}
      <section style={{ padding:'5rem 2rem', background:'#FAFAF8' }}>
        <div style={{ maxWidth:720, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:'3rem' }}>
            <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'clamp(1.8rem,4vw,2.5rem)', color:'#1A1A1A', margin:'0 0 0.75rem' }}>Frequently Asked Questions</h2>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', color:'#6B6B6B', fontSize:'1rem', margin:0 }}>Everything you need to know about Okoiya.</p>
          </div>
          <FAQList/>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section style={{ padding:'2rem 2rem 5rem' }}>
        <div style={{ background:'linear-gradient(135deg,#C4826A 0%,#D4A882 50%,#9BAF9B 100%)', borderRadius:'2rem', maxWidth:1100, margin:'0 auto', padding:'5rem 2rem', textAlign:'center' }}>
          <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'clamp(2rem,5vw,3rem)', color:'white', margin:'0 0 1rem' }}>Your forever starts here</h2>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'1rem', color:'rgba(255,255,255,0.85)', margin:'0 0 2.5rem', lineHeight:1.7 }}>
            Join 100+ couples already planning their wedding with Okoiya.
          </p>
          <Link to="/signup">
            <button style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'1rem', color:'#1A1A1A', background:'white', border:'none', borderRadius:100, padding:'1rem 2.5rem', cursor:'pointer' }}>
              Begin your story — it's free
            </button>
          </Link>
        </div>
      </section>

      <MarketingFooter/>
    </div>
  )
}
